import { readSetupParam } from "./eligibility";

/**
 * The welcome-flow override: run the flow for this account regardless of the
 * audience rules and the empty-tabs first-run check.
 *
 * Two ways in (see readSetupParam):
 *
 *   ?setup=start  — the link sales sends a customer. Works in production, so an
 *                   existing user, a lapsed one, or someone who has already
 *                   done it can be sent through it again. Always a reset.
 *   ?onboarding=  — force / reset / off, for dev and preview builds only.
 *
 * The param is captured into sessionStorage at module load because it does
 * NOT survive to render time: the Keycloak login round-trip returns to the
 * client's fixed redirect URI (host root, query stripped) and the tab system
 * rewrites the URL to ?tab=... — both destroy query params. The override
 * therefore lasts for the browser-tab session.
 *
 * It does not have to outlast it: a run started under the override records
 * that on onboarding.state (`startedVia`), and the gate honours the run from
 * there — otherwise opening a new tab mid-run would take the rail away from
 * anyone the audience rules would not have let in.
 */
export type OnboardingOverride = "force" | "reset" | null;

const OVERRIDE_KEY = "onboarding.override";
/**
 * Set when the override came from ?setup=start. Production honours only
 * these, so a dev param can never switch the override on there.
 */
const FROM_LINK_KEY = "onboarding.override.fromLink";
const isProduction = import.meta.env.VITE_APP_ENV === "production";

// Module-load capture — runs during bundle evaluation, before keycloak-js
// can begin any redirect that would strip the query string.
try {
  const param = readSetupParam(window.location.search, isProduction);
  if (param?.action === "off") {
    sessionStorage.removeItem(OVERRIDE_KEY);
    sessionStorage.removeItem(FROM_LINK_KEY);
  } else if (param) {
    sessionStorage.setItem(OVERRIDE_KEY, param.action);
    if (param.fromLink) sessionStorage.setItem(FROM_LINK_KEY, "1");
    else sessionStorage.removeItem(FROM_LINK_KEY);
  }
} catch {
  // sessionStorage unavailable — override simply stays off
}

export function getOnboardingOverride(): OnboardingOverride {
  try {
    const value = sessionStorage.getItem(OVERRIDE_KEY);
    if (value !== "force" && value !== "reset") return null;
    if (isProduction && sessionStorage.getItem(FROM_LINK_KEY) !== "1") {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

/** Adjust the stored override: "force" once a reset has run, "off" to clear. */
export function setOnboardingOverride(value: "force" | "off"): void {
  try {
    if (value === "off") {
      sessionStorage.removeItem(OVERRIDE_KEY);
      sessionStorage.removeItem(FROM_LINK_KEY);
    } else {
      sessionStorage.setItem(OVERRIDE_KEY, value);
    }
  } catch {
    // ignore
  }
}
